import type { AlertColor } from "@mui/material/Alert/Alert";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { useState } from "react";

import useQueryClient from "../../../api/query";
import useDevMode from "../../../utils/devmode";
import { CredentialStatus } from "../../credentialStatus";
import DataTable, { type SortModel } from "../../DataTable";
import { MASK } from "../../redact";
import CustomNoRowsOverlay from "../../TableNoDataOverlay";
import { CREDENTIAL_TYPE, MFA_METHOD, mfaJsonForDuplicate } from "../common";
import { AddPayerCredentials } from "../Form/AddPayerCredentials";
import type { AddPayerCredentialsFormFields } from "../Form/PayerCredentialsForm.zod";
import { CredentialsDeleteDialog } from "../SettingsDialog/CredentialsDeleteDialog";
import { CredentialsSettingsDialog } from "../SettingsDialog/CredentialsSettingsDialog";
import { CredentialsStatusDialog } from "../SettingsDialog/CredentialsStatusDialog";
import type { CredentialsStore } from "../store";
import {
  type CredentialRow,
  getDefaultColumns,
} from "./CredentialsTableColumns";

interface DuplicateProps {
  sources: SourceMessage[];
  pmses: PMSMessage[];
  practices: PracticeMessage[];
  userCredentials: CredentialsStore["userCredentials"];
  afterFormSubmit: ({
    sourceId,
    practiceId,
    username,
    password,
  }: Partial<UserCredentialsMessage>) => Promise<void>;
}

interface CredentialsTableProps {
  generateRow: () => CredentialRow[];
  practiceNames: string[];
  credentialNames: string[];
  supportedSourcesMap: Record<string, SourceMessage>;
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
  selectedPractices: PracticeMessage[];
  showTypeColumn?: boolean;
  duplicateProps?: DuplicateProps;
}

// `updatedAt` is not a column, so the table keeps the order it was given.
const INITIAL_SORT: SortModel = [{ field: "updatedAt", sort: "desc" }];

function filterCredentials(
  credentials: CredentialRow[],
  selectedPractices: PracticeMessage[],
  devMode: boolean,
): CredentialRow[] {
  const practiceIds = new Set(selectedPractices.map((p) => p.wieldyId));
  return credentials.filter(
    (credential) =>
      practiceIds.has(credential.practiceId ?? "") &&
      (devMode || credential.userType !== "HUMAN"),
  );
}

export function CredentialsTable({
  generateRow,
  practiceNames,
  credentialNames,
  supportedSourcesMap,
  updateSnackBar,
  selectedPractices,
  showTypeColumn = true,
  duplicateProps = undefined,
}: CredentialsTableProps) {
  const queryClient = useQueryClient();
  const devMode = useDevMode();
  const rows = filterCredentials(generateRow(), selectedPractices, devMode);
  const [selectedRow, setSelectedRow] =
    useState<Partial<UserCredentialsMessage> | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toggleStatusOpen, setToggleStatusOpen] = useState(false);
  const [duplicateValues, setDuplicateValues] =
    useState<Partial<AddPayerCredentialsFormFields> | null>(null);
  const openEditModal = async (credential: CredentialRow) => {
    let unmaskedMfaJson = credential.mfaJson;

    if (devMode && credential.sourceId) {
      try {
        unmaskedMfaJson = await credential.unmaskValue("mfa_json");
      } catch {
        // Ignore unmask errors
      }
    }

    const unmaskedCredential = {
      ...credential,
      username: await credential.unmaskValue("username"),
      password: await credential.unmaskValue("password"),
      mfaJson: unmaskedMfaJson,
    };
    setSelectedRow(unmaskedCredential);
    setEditOpen(true);
  };
  const closeEditModal = () => {
    setEditOpen(false);
    setSelectedRow(null);
  };

  const openDuplicateModal = async (credential: CredentialRow) => {
    const username = await credential.unmaskValue("username");
    const password = await credential.unmaskValue("password");
    if (username === MASK || password === MASK) {
      updateSnackBar({
        severity: "error",
        message: "Unable to duplicate: credential is not unmasked yet.",
      });
      return;
    }

    let unmaskedMfaJson = credential.mfaJson;
    try {
      unmaskedMfaJson = await credential.unmaskValue("mfa_json");
    } catch {
      // Ignore unmask errors; treated as no MFA below.
    }
    if (unmaskedMfaJson === MASK) {
      unmaskedMfaJson = undefined;
    }

    const { mfaJson, method } = mfaJsonForDuplicate(unmaskedMfaJson);
    if (
      credential.mfaType &&
      credential.mfaType !== "NONE" &&
      method === MFA_METHOD.NONE
    ) {
      updateSnackBar({
        severity: "info",
        message:
          "MFA settings could not be copied. Set them up on the duplicate manually.",
      });
    }

    setDuplicateValues({
      type: CREDENTIAL_TYPE.PAYER,
      practiceId: credential.practiceId,
      source: credential.sourceId,
      userType:
        credential.userType as AddPayerCredentialsFormFields["userType"],
      userId: credential.userId,
      username,
      password,
      website: credential.website || undefined,
      notes: credential.notes || undefined,
      mfaJson,
    });
  };
  const closeDuplicateModal = () => {
    setDuplicateValues(null);
  };

  const openDeleteModal = (credential: Partial<UserCredentialsMessage>) => {
    setSelectedRow(credential);
    setDeleteOpen(true);
  };
  const closeDeleteModal = () => {
    setDeleteOpen(false);
    setSelectedRow(null);
  };

  const openToggleStatus = (credential: Partial<UserCredentialsMessage>) => {
    setSelectedRow(credential);
    setToggleStatusOpen(true);
  };
  const closeToggleStatus = () => {
    setToggleStatusOpen(false);
    setSelectedRow(null);
  };
  const handleConfirmToggleStatus = async () => {
    if (!selectedRow?.wieldyId || !selectedRow.status) return;
    const newStatus =
      selectedRow.status === CredentialStatus.ACTIVE
        ? CredentialStatus.INACTIVE
        : CredentialStatus.ACTIVE;
    try {
      await queryClient.patchPayerCredentialStatus(
        selectedRow.wieldyId,
        newStatus,
      );
      // biome-ignore lint/suspicious/noExplicitAny: row carries an untyped update callback
      (selectedRow as any).updateUserCredential?.({ status: newStatus });
      updateSnackBar({
        severity: "success",
        message: `Credential set to ${newStatus.toLowerCase()}.`,
      });
      closeToggleStatus();
    } catch (error) {
      updateSnackBar({
        severity: "error",
        message: "Failed to update credential status. Try again.",
      });
      // biome-ignore lint/suspicious/noConsole: intentional browser console output
      console.error(`Failed to update credential status: ${error}`);
      throw error;
    }
  };

  return (
    <>
      <Box sx={{ padding: "1rem 0" }}>
        <DataTable
          clientSide
          columns={getDefaultColumns(
            practiceNames,
            credentialNames,
            supportedSourcesMap,
            openEditModal,
            openDeleteModal,
            openToggleStatus,
            devMode,
            showTypeColumn,
            duplicateProps ? openDuplicateModal : undefined,
          )}
          rows={rows}
          getRowId={(credential) => credential.id ?? credential.wieldyId ?? ""}
          initialSortModel={INITIAL_SORT}
          pageSizeOptions={[10, 20, 40]}
          noRowsOverlay={
            <CustomNoRowsOverlay
              title="No credentials"
              description="No credentials match the current filters."
            />
          }
        />
      </Box>
      {selectedRow && (
        <>
          <CredentialsSettingsDialog
            credentials={selectedRow}
            allCredentials={rows}
            updateSnackBar={updateSnackBar}
            supportedSourcesMap={supportedSourcesMap}
            open={editOpen}
            onClose={closeEditModal}
          />
          <CredentialsDeleteDialog
            credentials={selectedRow}
            updateSnackBar={updateSnackBar}
            open={deleteOpen}
            onClose={closeDeleteModal}
          />
          {(selectedRow.status === CredentialStatus.ACTIVE ||
            selectedRow.status === CredentialStatus.INACTIVE) && (
            <CredentialsStatusDialog
              credentials={selectedRow}
              newStatus={
                selectedRow.status === CredentialStatus.ACTIVE
                  ? CredentialStatus.INACTIVE
                  : CredentialStatus.ACTIVE
              }
              open={toggleStatusOpen}
              onClose={closeToggleStatus}
              onConfirm={handleConfirmToggleStatus}
            />
          )}
        </>
      )}
      {duplicateProps && duplicateValues && (
        <Dialog
          open
          onClose={closeDuplicateModal}
          sx={{
            "& .MuiPaper-root": {
              maxWidth: "735px",
              width: "100%",
              padding: "24px 36px",
              margin: "auto",
              borderRadius: "12px",
            },
          }}
        >
          <Box>
            <DialogContent sx={{ padding: 0, margin: 0 }}>
              <AddPayerCredentials
                sources={duplicateProps.sources}
                pmses={duplicateProps.pmses}
                practices={duplicateProps.practices}
                userCredentials={duplicateProps.userCredentials}
                initialValues={duplicateValues}
                cancel={closeDuplicateModal}
                afterFormSubmit={async (data) => {
                  closeDuplicateModal();
                  await duplicateProps.afterFormSubmit(data);
                }}
                updateSnackBar={updateSnackBar}
              />
            </DialogContent>
          </Box>
        </Dialog>
      )}
    </>
  );
}
